import store from '@/store'
import cognitoAuth from '@/cognito'
import { authHelpers } from '@/api/authHelpers'
import errorReporter from '@/utils/errorReporter'
import { isSessionExpiredError } from '@/utils/sessionExpired'

let cypressRunning
try {
  if (Cypress) {
    cypressRunning = true
  }
} catch (e) {
  cypressRunning = false
}

class GraphQLClient {
  constructor (endpoint) {
    if (cypressRunning) {
      this.endpoint = Cypress.env('VUE_APP_APPSYNC_GRAPHQL_ENDPOINT')
    } else {
      this.endpoint = process.env.VUE_APP_APPSYNC_GRAPHQL_ENDPOINT
    }
  }

  async getToken (rejectedIdToken) {
    let jwtToken
    try {
      jwtToken = await cognitoAuth.getIdTokenPromise({ rejectedIdToken })
    } catch (error) {
      throw isSessionExpiredError(error) ? Object.assign(error, { isSessionExpired: true }) : error
    }

    if (!jwtToken && store.state.isUserLoggedIn) {
      throw Object.assign(new Error('Unauthorized'), { isSessionExpired: true })
    }

    return jwtToken
  }

  post (body, jwtToken) {
    return fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: jwtToken
      },
      body
    })
  }

  async query ({ operationName, query, variables = {} }, { failSilently = false } = {}) {
    try {
      const body = JSON.stringify({ operationName, query, variables })
      const jwtToken = await this.getToken()
      let response = await this.post(body, jwtToken)

      if (response.status === 401 && jwtToken) {
        response = await this.post(body, await this.getToken(jwtToken))

        if (response.status !== 401) {
          errorReporter.reportError({ message: 'Refused token replaced', type: 'auth-retry', extra: { operationName } })
        }
      }

      if (response.status === 401) {
        throw new Error('Unauthorized')
      }

      if (!response.ok) {
        throw new Error(`Network error: ${response.status} - ${response.statusText}`)
      }

      const jsonResponse = await response.json()

      // Error handling for GraphQL errors
      if (jsonResponse.errors) {
        const message = jsonResponse.errors.map((error) => error.message).join('\n')
        throw new Error(`GraphQL error: ${message}`)
      }

      return jsonResponse.data
    } catch (error) {
      errorReporter.reportError({
        message: error.message,
        stack: error.stack || '',
        type: 'graphql-error',
        extra: { operationName }
      })

      // Anonymous (public link / embed) viewers have no session to expire, so don't
      // bounce them to login.
      if (store.state.isUserLoggedIn && error.isSessionExpired) {
        authHelpers.expireSession(null, 'request')
      }

      if (failSilently) {
        return
      }

      throw error
    }
  }
}

const client = new GraphQLClient()
export default client
